import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";
import SuiDatePicker from "components/SuiDatePicker";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import CollectionsIcon from "@mui/icons-material/Collections";
import StyleIcon from "@mui/icons-material/Style";
import PaletteIcon from "@mui/icons-material/Palette";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";
import HorizontalRuleOutlinedIcon from "@mui/icons-material/HorizontalRuleOutlined";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { DatePicker, Tooltip, Upload } from "antd";
import Prompt from "components/UpStyled/Prompt";
import ColorDropDownMenu from "pages/calendar/components/ColorDropDownMenu";

import { filterParser, matchResourceUrl } from "utils/helper";
import { colorOptions } from "utils/data";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import ImportStyleModal2 from "pages/de/project/create-project/components/ImportStyleModal2";
import ImportMoodboardModal from "pages/de/collection/detail/components/ImportMoodboardModal";
import ImportProjectModal from "pages/de/project/components/ImportProjectModal";
import ImportCollectionModal from "pages/de/collection/components/ImportCollectionModal";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { useNavigate, useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import ReactGA from "react-ga4";
import parse from "html-react-parser";

const POPUP_ZINDEX = 1050;

const notificationOption = [
  {
    label: "5 minutes before",
    value: 300,
  },
  {
    label: "10 minutes before",
    value: 600,
  },
  {
    label: "15 minutes before",
    value: 900,
  },
  {
    label: "30 minutes before",
    value: 1800,
  },
  {
    label: "1 hour before",
    value: 3600,
  },
  {
    label: "1 day before",
    value: 86400,
  },
];

const EventModalViewer = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      handleOpenEdit,
      selectInfo = {},
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
      selectedType,
    },
    ref
  ) => {
    const [isAllDay, setIsAllDay] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [editEventVisible, setEditEventVisible] = useState(false);
    const [type, setType] = useState(selectedType ? selectedType : "task");
    const [selectedStartDate, setSelectedStartDate] = useState(moment().startOf("day").toDate());
    const [selectedStartTime, setSelectedStartTime] = useState(moment().startOf("minute").toDate());
    const [selectedEndDate, setSelectedEndDate] = useState(moment().startOf("day").toDate());
    const [selectedEndTime, setSelectedEndTime] = useState(
      moment().startOf("minute").add(30, "minutes").toDate()
    );
    const [userOptions, setUserOptions] = useState([]);
    const [calendarOptions, setCalendarOptions] = useState();
    const [selectedColor, setSelectedColor] = useState({});

    const [selectedResource, setSelectedResource] = useState([]);
    const [fileList, setFileList] = useState([]);
    const [selectedCalendar, setSelectedCalendar] = useState({});

    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    const account = useContext(AccountContext);
    const isOwner = account.id == selectInfo?.event?.extendedProps.owner_id;
    const notifySeconds = selectInfo?.event?.extendedProps?.content?.notification;

    useEffect(() => {
      handleRetrieveCalendars();
    }, []);

    useEffect(() => {
      ReactGA.event("view_calendar_modal", { user: account.id, value: 1 });
    }, []);

    useEffect(() => {
      //data store in selectInfo.event

      setIsAllDay(selectInfo?.event?.allDay);
      setType(selectInfo?.event?.extendedProps?.type);
      setFileList(selectInfo?.event?.extendedProps?.content?.attachFiles || []);

      if (selectInfo?.event?.start) {
        const startDate = selectInfo?.event?.start;
        setSelectedStartDate(startDate);
      }

      if (selectInfo?.event?.end) {
        const endDate = selectInfo?.event?.end;
        setSelectedEndDate(endDate);
      }

      if (selectInfo?.event?.extendedProps?.content?.resources) {
        setSelectedResource(selectInfo?.event?.extendedProps?.content?.resources);
      }
    }, [selectInfo]);

    const handleRetrieveCalendars = async () => {
      try {
        const { data } = await api.calendar.retrieveCalendar(
          selectInfo.event?.extendedProps?.calendar_id
        );
        setSelectedCalendar(data?.calendar || {});

        const defaultColor = selectInfo?.event?.backgroundColor;
        let colorOpt = colorOptions.find((x) => x.value == defaultColor);
        if (colorOpt?.value) {
          setSelectedColor(colorOpt);
        } else {
          setSelectedColor({
            label: defaultColor,
            value: defaultColor,
          });
        }
      } catch (err) {
        console.log(err);
        toast.error(`Could not retrieve calendars!`);
      }
    };

    const handleMarkStatus = async (status) => {
      if (selectInfo?.view?.calendar) {
        let calendarApi = selectInfo.view.calendar;
        calendarApi.unselect(); // clear date selection
      }

      try {
        let payload = {
          status: status,
        };

        if (!selectInfo?.event?.id) {
          return toast.error("Could not find the event ID!");
        }
        const newCreatedEvent = await api.calendar.updateEvent(selectInfo?.event?.id, payload);
        toast.success(`Mark ${status} successfully!`);
        handleClose();
        handleAfterOk(newCreatedEvent);

        setTimeout(() => {
          handleRefresh();
        }, refreshDelay);
      } catch (err) {
        console.log(err);
        toast.error(`Mark ${status} failed!`);
      }
    };

    const handleConfirmDelete = async () => {
      try {
        await api.calendar.deleteEvent(selectInfo?.event?.id);
        handleRefresh();
        setDeleteVisible(false);
        handleClose();
        toast.success(`Delete event:${selectInfo?.event?.title} successfully!`);
      } catch (err) {
        toast.error(`Delete event:${selectInfo?.event?.title} failed!`);
        console.log(err);
      }
    };

    const description = selectInfo?.event?.extendedProps?.content?.description;

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth={true}
        style={{ zIndex: POPUP_ZINDEX }}
        disableEnforceFocus
      >
        <DialogTitle id="alert-dialog-title" sx={{ textTransform: "capitalize" }}>
          <SuiBox display="flex" justifyContent="flex-end">
            {isOwner ? (
              <Tooltip title={`Delete ${type}`}>
                <IconButton
                  aria-label="delete"
                  onClick={() => setDeleteVisible(true)}
                  sx={{ "&:hover": { backgroundColor: (theme) => theme.palette.grey[200] } }}
                >
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            {handleOpenEdit && isOwner ? (
              <Tooltip title={`Edit ${type}`}>
                <IconButton
                  aria-label="edit"
                  onClick={handleOpenEdit}
                  sx={{ "&:hover": { backgroundColor: (theme) => theme.palette.grey[200] } }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title={`Close`}>
              <IconButton
                aria-label="close"
                onClick={() => handleClose()}
                sx={{ "&:hover": { backgroundColor: (theme) => theme.palette.grey[200] } }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </SuiBox>
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <SuiBox sx={{ px: 1 }}>
              <SuiBox mb={0.5} sx={{ width: "100%" }}>
                <SuiBox ml={4} mb={1}>
                  <SuiTypography
                    variant="h5"
                    textTransform="capitalize"
                    sx={{ display: "inline", mr: 0.5 }}
                  >
                    {type}:
                  </SuiTypography>
                  <SuiTypography sx={{ display: "inline" }} variant="h5">
                    {selectInfo?.event?.title}
                  </SuiTypography>
                </SuiBox>
              </SuiBox>

              <SuiBox my={1}>
                <SuiBox display="flex" alignItems="center" gap={1}>
                  <SuiBox>
                    <ScheduleIcon fontSize="medium" />
                  </SuiBox>
                  <SuiBox>
                    <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SuiBox>
                        <SuiTypography variant="body1" sx={{ lineHeight: 0 }}>
                          {moment(selectedStartDate).format("dddd, MMMM Do")}
                        </SuiTypography>
                        {!isAllDay ? (
                          <SuiTypography variant="caption" sx={{ lineHeight: 0 }}>
                            {moment(selectedStartDate).format("h:mm a")}
                          </SuiTypography>
                        ) : null}
                      </SuiBox>
                      {type == "task" || type == "event" ? <HorizontalRuleOutlinedIcon /> : null}
                      {type == "task" || type == "event" ? (
                        <SuiBox>
                          <SuiTypography variant="body1" sx={{ lineHeight: 0 }}>
                            {moment(selectedEndDate).format("dddd, MMMM Do")}
                          </SuiTypography>
                          {!isAllDay ? (
                            <SuiTypography variant="caption" sx={{ lineHeight: 0 }}>
                              {moment(selectedEndDate).format("h:mm a")}
                            </SuiTypography>
                          ) : null}
                        </SuiBox>
                      ) : null}
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
              {type == "event_todo" ? (
                <SuiBox my={1}>
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    <SuiBox>
                      <GroupAddOutlinedIcon fontSize="medium" />
                    </SuiBox>
                    <SuiSelect
                      placeholder="Add Guests"
                      options={userOptions}
                      {...register("guests")}
                    />
                  </SuiBox>
                </SuiBox>
              ) : null}
              {description ? (
                <SuiBox my={1}>
                  <SuiBox display="flex" alignItems="flex-start" gap={1}>
                    <SuiBox>
                      <SubjectOutlinedIcon fontSize="medium" />
                    </SuiBox>
                    <SuiBox width="100%">
                      {type == "event" ? (
                        <SuiTypography variant="body1">{parse(description)}</SuiTypography>
                      ) : (
                        <SuiTypography variant="body1"> {description}</SuiTypography>
                      )}
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              ) : null}

              {type == "task" && selectedResource.length > 0 ? (
                <SuiBox my={1}>
                  <SuiBox display="flex" alignItems="flex-start" gap={1}>
                    <SuiBox>
                      <DesignServicesIcon fontSize="medium" />
                    </SuiBox>
                    <SuiBox sx={{ width: "100%" }}>
                      {selectedResource.map((item) => {
                        let img = {};
                        let title = "";
                        if (item.resourceType == "style") {
                          const styleImages = getStyleImages(item, false);
                          img = styleImages[0];
                          title = item?.title;
                        } else if (item.resourceType == "moodboard") {
                          img = item.img;
                          title = item?.name;
                        } else if (item.resourceType == "project") {
                          const products = item?.products || [];
                          let styleImages = [];
                          //add style first image
                          for (let i = 0; i < products.length; i++) {
                            const temp = getStyleImages(products[i], false);
                            styleImages.push(...temp);
                          }
                          img = styleImages[0];
                          title = item?.name;
                        } else if (item.resourceType == "collection") {
                          img = item.moodboard?.[0]?.img;
                          title = item?.title;
                        }

                        return (
                          <SuiBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            sx={{ mb: 0.5 }}
                          >
                            <SuiBox display="flex" alignItems="center" gap={0.5}>
                              <SuiBox
                                sx={{ width: 40, height: 40, cursor: "pointer" }}
                                onClick={(e) => {
                                  window.open(matchResourceUrl(item), "_blank");
                                }}
                              >
                                <ImageCardWrapper
                                  data={img}
                                  mode={"normal"}
                                  height={40}
                                  width={40}
                                  iconMode
                                />{" "}
                              </SuiBox>
                              <SuiTypography variant="body1">{title}</SuiTypography>
                            </SuiBox>
                            <SuiBox display="flex" alignItems="center" gap={0.5}>
                              <SuiBox>
                                <Tooltip title="View detail">
                                  <PageviewOutlinedIcon
                                    color="dark"
                                    fontSize="small"
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                      window.open(matchResourceUrl(item), "_blank");
                                    }}
                                  />
                                </Tooltip>{" "}
                              </SuiBox>
                            </SuiBox>
                          </SuiBox>
                        );
                      })}
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              ) : null}
              {type != "reminder" && fileList.length > 0 ? (
                <SuiBox my={1}>
                  <SuiBox display="flex" alignItems="flex-start" gap={1}>
                    <SuiBox>
                      <AttachFileOutlinedIcon fontSize="medium" />
                    </SuiBox>
                    <SuiBox>
                      {fileList.map((x) => (
                        <div>
                          <SuiTypography
                            variant="body1"
                            sx={{ minWidth: 50, lineHeight: 0 }}
                            component="a"
                            download
                            href={x.url}
                            target="_blank"
                          >
                            {x.name}
                          </SuiTypography>
                        </div>
                      ))}
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              ) : null}
              <SuiBox my={1}>
                <SuiBox display="flex" alignItems="center" gap={1}>
                  <SuiBox>
                    <InsertInvitationOutlinedIcon fontSize="medium" />
                  </SuiBox>
                  <SuiBox display="flex" gap={1} alignItems="center">
                    <SuiTypography variant="body1" sx={{ minWidth: 50 }}>
                      {selectedCalendar?.title}
                    </SuiTypography>
                    <ColorDropDownMenu color={selectedColor} onClick={null} />
                  </SuiBox>
                </SuiBox>
              </SuiBox>
              {type == "event" && notifySeconds ? (
                <SuiBox my={1}>
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    <SuiBox>
                      <NotificationsOutlinedIcon fontSize="medium" />
                    </SuiBox>
                    <SuiBox display="flex" alignItems="center" gap={1}>
                      <SuiTypography variant="body1">
                        {moment.duration(notifySeconds, "seconds").humanize()}
                      </SuiTypography>
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              ) : null}
            </SuiBox>

            <SuiBox
              pt={2}
              px={3}
              display="flex"
              justifyContent="space-between"
              alignItems={"center"}
              gap={1}
            >
              <SuiBox></SuiBox>
              <SuiBox display="flex" gap={1} alignItems="center" justifyContent="end">
                {type == "task" && isOwner ? (
                  selectInfo?.event?.extendedProps?.status == "uncompleted" ? (
                    <SuiButton
                      variant="text"
                      size="medium"
                      onClick={() => handleMarkStatus("completed")}
                      color="dark"
                      textTransform="none"
                    >
                      Mark completed
                    </SuiButton>
                  ) : (
                    <SuiButton
                      variant="text"
                      size="medium"
                      onClick={() => handleMarkStatus("uncompleted")}
                      color="dark"
                      textTransform="none"
                    >
                      Mark uncompleted
                    </SuiButton>
                  )
                ) : null}
                <SuiButton size="medium" variant="gradient" color="dark" onClick={handleClose}>
                  Done
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </DialogContentText>
        </DialogContent>
        {deleteVisible && selectInfo?.event?.id ? (
          <Prompt
            heading="Delete Event"
            text={`Are you sure you want to delete the event: ${selectInfo?.event?.title}`}
            visible={deleteVisible}
            okText="Delete"
            okColor="error"
            handleOk={() => {
              handleConfirmDelete();
            }}
            handleClose={() => setDeleteVisible(false)}
            option={{ sx: { zIndex: POPUP_ZINDEX + 2 } }}
          />
        ) : null}
      </Dialog>
    );
  }
);

export default EventModalViewer;
